import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThumbsUp, ThumbsDown, Copy, MoreHorizontal, Flag } from 'lucide-react';

interface MessageActionsProps {
  messageId: string;
  onReaction?: (emoji: string, action: 'add' | 'remove') => void;
  onCopy?: (content: string) => void;
  onReport?: () => void;
  className?: string;
}

const MessageActions: React.FC<MessageActionsProps> = ({
  messageId,
  onReaction,
  onCopy,
  onReport,
  className = '',
}) => {
  const [showActions, setShowActions] = useState(false);
  const [reactions, setReactions] = useState<{ [key: string]: number }>({
    '👍': 0,
    '👎': 0,
  });

  const handleReaction = (emoji: string) => {
    const currentCount = reactions[emoji] || 0;
    const newCount = currentCount === 0 ? 1 : 0;
    
    setReactions(prev => ({
      ...prev,
      [emoji]: newCount,
    }));

    if (onReaction) {
      onReaction(emoji, newCount > 0 ? 'add' : 'remove');
    }
  };

  const handleCopy = () => {
    if (onCopy) {
      onCopy('Message content copied!');
    }
    // You can implement actual copy functionality here
  };

  const handleReport = () => {
    if (onReport) {
      onReport();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main action buttons */}
      <div className="flex items-center gap-1">
        {/* Thumbs up */}
        <button
          onClick={() => handleReaction('👍')}
          className={`p-1.5 rounded-full transition-all duration-200 ${
            reactions['👍'] > 0
              ? 'bg-green-100 text-green-600'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
        >
          <ThumbsUp size={16} />
        </button>

        {/* Thumbs down */}
        <button
          onClick={() => handleReaction('👎')}
          className={`p-1.5 rounded-full transition-all duration-200 ${
            reactions['👎'] > 0
              ? 'bg-red-100 text-red-600'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
        >
          <ThumbsDown size={16} />
        </button>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
          title="Copy message"
        >
          <Copy size={16} />
        </button>

        {/* More actions button */}
        <button
          onClick={() => setShowActions(!showActions)}
          className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
        >
          <MoreHorizontal size={16} />
        </button>
      </div>

      {/* Reaction counts */}
      {(reactions['👍'] > 0 || reactions['👎'] > 0) && (
        <div className="flex items-center gap-1 mt-1">
          {reactions['👍'] > 0 && (
            <span className="text-xs text-green-600 font-medium">{reactions['👍']}</span>
          )}
          {reactions['👎'] > 0 && (
            <span className="text-xs text-red-600 font-medium">{reactions['👎']}</span>
          )}
        </div>
      )}

      {/* Dropdown actions */}
      <AnimatePresence>
        {showActions && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[120px]"
          >
            <button
              onClick={handleReport}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors duration-200"
            >
              <Flag size={14} />
              Report
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageActions; 